import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Chip
} from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { approveTransfer, rejectTransfer } from '../services/transfersAPI';

const TransferApprovalModal = ({ open, transfer, onClose, onSuccess }) => {
  const [action, setAction] = useState(null); // 'approve' or 'reject'
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleApprove = async () => {
    try {
      setLoading(true);
      setError('');
      await approveTransfer(transfer.id, comments);
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve transfer');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!comments.trim()) {
      setError('Rejection reason is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await rejectTransfer(transfer.id, comments);
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reject transfer');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setAction(null);
    setComments('');
    setError('');
    onClose();
  };

  if (!transfer) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {action ? (action === 'approve' ? 'Approve Transfer' : 'Reject Transfer') : 'Review Transfer'}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Transfer Details */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Transfer Number:</strong> {transfer.transferNumber}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Material:</strong> {transfer.materialName}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Quantity:</strong> {transfer.quantity} {transfer.unit}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>From:</strong> {transfer.fromSite?.siteName}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>To:</strong> {transfer.toSite?.siteName}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Requested By:</strong> {transfer.requester?.name}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Reason:</strong> {transfer.reason}
          </Typography>
          {transfer.notes && (
            <Typography variant="body2">
              <strong>Notes:</strong> {transfer.notes}
            </Typography>
          )}
        </Box>

        {/* Action Buttons */}
        {!action && (
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              fullWidth
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => setAction('approve')}
            >
              Approve
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="error"
              startIcon={<Cancel />}
              onClick={() => setAction('reject')}
            >
              Reject
            </Button>
          </Box>
        )}

        {/* Comments Field */}
        {action && (
          <TextField
            fullWidth
            multiline
            rows={3}
            label={action === 'approve' ? 'Comments (Optional)' : 'Rejection Reason *'}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            required={action === 'reject'}
            placeholder={action === 'approve' ? 'Add any comments...' : 'Explain why this transfer is being rejected...'}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        {action && (
          <Button
            variant="contained"
            color={action === 'approve' ? 'success' : 'error'}
            onClick={action === 'approve' ? handleApprove : handleReject}
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? 'Processing...' : `Confirm ${action === 'approve' ? 'Approval' : 'Rejection'}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TransferApprovalModal;